'use client'

// libraries
import { useEffect } from 'react'

// Helper function to wrap tables in a container
const wrapTables = (container: Element) => {
    const tables = container.querySelectorAll('table')
    
    tables.forEach((table) => {
        if (!table.parentElement?.classList.contains('table-rich-text')) {
            const wrapper = document.createElement('div')
            wrapper.classList.add('table-rich-text')
            
            table.parentNode?.insertBefore(wrapper, table)
            wrapper.appendChild(table)
        }
    })
}

// Helper function to process accordion rich text elements
const processAccordionRichText = () => {
    const richTextInsideAccordionsElements = document.querySelectorAll('.accordion .rich-text')
    richTextInsideAccordionsElements.forEach((richTextElement) => {
        wrapTables(richTextElement)
    })
}

export default function useAccordionTableWrapper() {
    useEffect(() => {
        const handleDomReady = () => {
            processAccordionRichText()
        }
    
        // wait for the dom to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', handleDomReady)
        } else {
            handleDomReady()
        }

        // Use MutationObserver to watch for changes in accordion content
        // This handles cases where dangerouslySetInnerHTML replaces content
        const observer = new MutationObserver((mutations) => {
            let shouldReprocess = false
            
            mutations.forEach((mutation) => {
                // Watch for when innerHTML is replaced (dangerouslySetInnerHTML updates)
                if (mutation.type === 'childList') {
                    // Check if tables were added or if rich-text content changed
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element
                            // If a table was added, or if rich-text content was updated
                            if (element.tagName === 'TABLE' || 
                                element.classList?.contains('rich-text') || 
                                element.querySelector?.('table') ||
                                element.querySelector?.('.rich-text')) {
                                shouldReprocess = true
                            }
                        }
                    })
                }
            })

            // Reprocess accordion rich-text elements when content changes
            if (shouldReprocess) {
                const richTextInsideAccordionsElements = document.querySelectorAll('.accordion .rich-text')
                richTextInsideAccordionsElements.forEach((richTextElement) => {
                    wrapTables(richTextElement)
                })
            }
        })

        // Observe changes in accordion content areas specifically
        const observeAccordions = () => {
            const accordionContainers = document.querySelectorAll('.accordion__content')
            accordionContainers.forEach((container) => {
                observer.observe(container, {
                    childList: true,
                    subtree: true,
                    attributes: false,
                    characterData: false
                })
            })
        }

        // Initial observation
        observeAccordions()

        // Re-observe when new accordions are added (in case of dynamic content)
        const bodyObserver = new MutationObserver(() => {
            observeAccordions()
        })
        
        bodyObserver.observe(document.body, {
            childList: true,
            subtree: true
        })
    
        return () => {
            document.removeEventListener('DOMContentLoaded', handleDomReady)
            observer.disconnect()
            bodyObserver.disconnect()
        }
    }, [])

    return null
}